/**
 * @file Trade and tradesperson management functions for Supabase
 */

import { supabase } from "@/integrations/supabase/client";
import { Tradesperson, UserTradesperson } from "@/lib/types";
import { TradespersonInsert, TradespersonUpdate } from "@/__generated__/database.types";
import { debug } from "@/lib/utils";
import { handleApiError, withErrorHandling } from "@/lib/errors";

/**
 * Maps a category from keywordMap to search terms for finding related tradespeople
 * This helps bridge the gap between our category naming and tradesperson terminology
 * @param category - The category string from keywordMap (e.g., "plumbing", "heating")
 * @returns An array of search terms relevant to that category
 */
function mapCategoryToTradeTerms(category: string): string[] {
  const mapping: Record<string, string[]> = {
    "heating": ["heating", "plumber", "gas", "boiler", "heat", "hvac", "Heat & Plumbing"],
    "plumbing": ["plumber", "plumbing", "pipe", "water", "Heat & Plumbing"],
    "electrics": ["electrician", "electrical", "electric", "wiring", "Electrics"],
    "roof_outside": ["roofer", "roofing", "guttering", "exterior", "Roof & Outside"],
    "chimney": ["chimney", "fireplace", "sweep", "flue", "Roof & Outside"],
    "garden": ["gardener", "gardening", "landscaper", "landscaping", "Garden & Plants"],
    "tree_surgery": ["tree", "arborist", "surgeon", "pruning", "Garden & Plants"],
    "decorating": ["decorator", "painting", "painter", "wallpaper", "DIY & Decoration"],
    "windows": ["glazier", "window", "glass", "door", "glazing", "DIY & Decoration"],
    "handyperson": ["handyman", "handyperson", "general", "repair", "maintenance", "DIY & Decoration"]
  };

  return mapping[category] || [category];
}

/**
 * Retrieves all tradespeople associated with a specific user
 * @param userId - The unique identifier of the user
 * @returns Promise containing an array of user-tradesperson relationships
 */
export const getUserTradespeople = withErrorHandling(
  async (userId: string) => {
    const { data, error } = await supabase
      .from("user_tradespeople")
      .select(`
        *,
        tradesperson:tradesperson_id(*)
      `)
      .eq("user_id", userId);

    if (error) throw error;
    return data || [] as UserTradesperson[];
  },
  [], // Fallback value
  'Failed to load tradespeople'
);

/**
 * Adds a tradesperson to a user's list and appends user's postcode to tradesperson's service areas
 * @param userId - The unique identifier of the user
 * @param tradespersonId - The unique identifier of the tradesperson
 * @param data - Additional data for the relationship
 * @returns Promise containing the created relationship data
 */
export async function addUserTradesperson(userId: string, tradespersonId: string, data: {
  notes?: string;
  preferred?: boolean;
}) {
  try {
    // First check if the relationship already exists to avoid duplicates
    const { data: existingData, error: checkError } = await supabase
      .from("user_tradespeople")
      .select("id")
      .eq("user_id", userId)
      .eq("tradesperson_id", tradespersonId)
      .maybeSingle();

    if (checkError) throw checkError;

    // If relationship already exists, return it
    if (existingData) {
      return existingData;
    }

    // Create new relationship
    const { data: responseData, error } = await supabase
      .from("user_tradespeople")
      .insert([{
        user_id: userId,
        tradesperson_id: tradespersonId,
        ...data
      }])
      .select();

    if (error) throw error;

    // Get the user's postcode to append to the tradesperson's service areas
    const { data: userData, error: userError } = await supabase
      .from("profiles")
      .select("postcode")
      .eq("id", userId)
      .single();

    if (!userError && userData && userData.postcode) {
      // Get the tradesperson's current postcodes
      const { data: tradeData, error: tradeError } = await supabase
        .from("tradespeople")
        .select("postcode")
        .eq("id", tradespersonId)
        .single();

      if (!tradeError && tradeData) {
        // Format user's postcode and ensure it's uppercase
        const userPostcode = userData.postcode.toUpperCase();

        // Prepare the updated postcode array
        let updatedPostcodes: string[] = [];

        // Handle existing postcode array or initialize a new one
        if (Array.isArray(tradeData.postcode)) {
          updatedPostcodes = [...tradeData.postcode];
        }

        // Only add the postcode if it's not already in the array
        if (userPostcode && !updatedPostcodes.includes(userPostcode)) {
          updatedPostcodes.push(userPostcode);

          // Update the tradesperson with the new postcode array
          const { error: updateError } = await supabase
            .from("tradespeople")
            .update({ postcode: updatedPostcodes })
            .eq("id", tradespersonId);

          if (updateError) {
            debug.error('Failed to update tradesperson postcodes:', updateError);
          }
        }
      }
    }

    // Update recommendation count for this tradesperson
    if (responseData && responseData.length > 0) {
      // Get current count
      const { data: tradeData, error: tradeError } = await supabase
        .from("tradespeople")
        .select("recommendation_count")
        .eq("id", tradespersonId)
        .single();

      if (!tradeError && tradeData) {
        // Increment count
        const currentCount = tradeData.recommendation_count || 0;
        const { error: updateError } = await supabase
          .from("tradespeople")
          .update({ recommendation_count: currentCount + 1 })
          .eq("id", tradespersonId);

        if (updateError) {
          debug.error('Failed to update recommendation count:', updateError);
        }
      }
    }

    return responseData;
  } catch (error) {
    handleApiError(error, 'Failed to add tradesperson');
    throw error;
  }
}

/**
 * Update a user-tradesperson relationship with notes or preferred status
 * @param id - The relation ID to update
 * @param data - Object containing notes and/or preferred flag
 * @returns The updated relation data
 */
export async function updateUserTradesperson(id: string, data: {
  notes?: string;
  preferred?: boolean;
}) {
  try {
    const { data: responseData, error } = await supabase
      .from("user_tradespeople")
      .update(data)
      .eq("id", id)
      .select();

    if (error) throw error;
    return responseData;
  } catch (error) {
    handleApiError(error, 'Failed to update tradesperson');
    throw error;
  }
}

/**
 * Removes a tradesperson from a user's list
 * @param id - The unique identifier of the relationship to delete
 * @returns Promise containing the deleted relationship data
 */
export async function deleteUserTradesperson(id: string) {
  try {
    const { data, error } = await supabase
      .from("user_tradespeople")
      .delete()
      .eq("id", id);

    if (error) throw error;
    return data;
  } catch (error) {
    handleApiError(error, 'Failed to remove tradesperson');
    throw error;
  }
}

/**
 * Searches for tradespeople in the global directory
 * @param keyword - The search keyword or category
 * @param tradeTypes - Optional array of trade types to filter by
 * @returns Promise containing an array of matching tradespeople
 */
export const searchGlobalTradespeople = withErrorHandling(
  async (keyword: string, tradeTypes?: string[]) => {
    // Get search terms for this category
    const searchTerms = mapCategoryToTradeTerms(keyword);

    // Start building the query
    let query = supabase
      .from("tradespeople")
      .select("*");

    // If trade types are provided, filter by them
    if (tradeTypes && tradeTypes.length > 0) {
      query = query.in("trade_type", tradeTypes);
    } else {
      // Otherwise, use the traditional search approach
      // Build search conditions using the correct field names prioritizing trade_type
      const searchConditions = searchTerms.map(term =>
        `trade_type.ilike.%${term}%,org.ilike.%${term}%,contact.ilike.%${term}%,category.ilike.%${term}%`
      ).join(',');

      debug.log('Search conditions:', searchConditions);

      query = query.or(searchConditions);
    }

    // Execute the query
    const { data, error } = await query.limit(10);

    if (error) throw error;
    debug.log('Search results:', data?.length || 0);
    return data || [] as Tradesperson[];
  },
  [], // Fallback value
  'Failed to search for tradespeople'
);

/**
 * Creates a new tradesperson entry
 * @param data - The tradesperson data to create
 * @returns Promise containing the created tradesperson data
 */
export async function insertTradesperson(data: TradespersonInsert) {
  try {
    // Ensure postcode is an array (handle backward compatibility)
    const normalizedData = { ...data };

    // Convert string postcode to array or initialize empty array
    if (typeof normalizedData.postcode === 'string') {
      // If it's a non-empty string, make it a single-element array
      normalizedData.postcode = normalizedData.postcode ? [normalizedData.postcode] : [];
    } else if (normalizedData.postcode === null || normalizedData.postcode === undefined) {
      // If null or undefined, initialize as empty array
      normalizedData.postcode = [];
    }

    // Convert any postcodes to uppercase for consistency
    if (Array.isArray(normalizedData.postcode)) {
      normalizedData.postcode = normalizedData.postcode
        .filter(p => p) // Remove empty strings
        .map(p => p.toUpperCase()); // Normalize to uppercase
    }

    const { data: responseData, error } = await supabase
      .from("tradespeople")
      .insert([normalizedData])
      .select();

    if (error) throw error;
    return { data: responseData as Tradesperson[] };
  } catch (error) {
    handleApiError(error, 'Failed to create tradesperson');
    throw error;
  }
}

/**
 * Updates the verification status of a tradesperson
 * @param id - The unique identifier of the tradesperson
 * @param verified - The new verification status
 * @returns Promise containing the updated tradesperson data
 */
export async function updateTradespersonVerification(id: string, verified: boolean) {
  try {
    const { data, error } = await supabase
      .from("tradespeople")
      .update({ verified })
      .eq("id", id)
      .select();

    if (error) throw error;
    return data as Tradesperson[];
  } catch (error) {
    handleApiError(error, 'Failed to update verification status');
    throw error;
  }
}

/**
 * Retrieves tradespeople in a specific category or trade type
 * @param categoryOrType - The trade category or trade_type to filter by
 * @param fieldName - Which field to filter on ('category' or 'trade_type'), defaults to 'category' for backward compatibility
 * @returns Promise containing an array of matching tradespeople
 */
export const getLocalTradespeople = withErrorHandling(
  async (categoryOrType: string, fieldName: 'category' | 'trade_type' = 'category') => {
    const { data, error } = await supabase
      .from("tradespeople")
      .select("*")
      .eq(fieldName, categoryOrType)
      .eq("verified", true);

    if (error) throw error;
    return data as Tradesperson[];
  },
  [], // Fallback value
  'Failed to load tradespeople'
);

/**
 * Gets tradespeople in a specific postcode area
 * @param postcodePrefix The postcode prefix to filter by (e.g. 'HP10')
 * @param limit Maximum number of results to return
 * @returns Promise containing tradespeople in the specified area
 */
export const getPostcodeTradespeople = withErrorHandling(
  async (postcodePrefix: string, limit = 10) => {
    // Format postcode prefix to uppercase for consistent matching
    const formattedPrefix = postcodePrefix.toUpperCase();

    debug.log('Getting tradespeople for postcode prefix:', formattedPrefix);

    try {
      // Step 1: We'll fetch all tradespeople and their postcodes
      // This is a simple and safe query that's guaranteed to work
      const { data: allTradespeople, error } = await supabase
        .from("tradespeople")
        .select("*")
        .eq("verified", true)
        .limit(100); // Get a reasonable number to filter locally

      if (error) {
        debug.error('Error fetching tradespeople:', error);
        throw error;
      }

      // Step 2: Filter the results client-side for postcodes matching our prefix
      // This avoids ALL SQL injection risks and complex query issues
      const filteredTradespeople = allTradespeople.filter(tradesperson => {
        // Make sure postcode array exists
        if (!tradesperson.postcode || !Array.isArray(tradesperson.postcode)) {
          return false;
        }

        // Check if any postcode in the array starts with our formattedPrefix
        return tradesperson.postcode.some(postcode => {
          if (typeof postcode !== 'string') return false;
          return postcode.toUpperCase().startsWith(formattedPrefix);
        });
      });

      // Log how many we found
      debug.log(`Found ${filteredTradespeople.length} tradespeople matching postcode prefix ${formattedPrefix}`);

      // Return the filtered results with the expected structure
      // Limit to the requested number
      return filteredTradespeople.slice(0, limit).map(trade => ({
        ...trade,
        recommendation_count: 0 // Default to 0 since we're not calculating from join
      })) as Tradesperson[];
    } catch (error) {
      debug.error('Postcode filtering failed:', error);
      throw error;
    }
  },
  [], // Fallback value
  'Failed to load tradespeople for area'
);

/**
 * Gets recently used tradespeople for a specific user
 * @param userId The user to get recent tradespeople for
 * @param limit Maximum number of results to return
 * @returns Promise containing recently used tradespeople
 */
export const getRecentTradespeople = withErrorHandling(
  async (userId: string, limit = 3) => {
    const { data, error } = await supabase
      .from("user_tradespeople")
      .select(`
        *,
        tradesperson:tradesperson_id(*)
      `)
      .eq("user_id", userId)
      .order('created_at', { ascending: false })
      .limit(limit);

    if (error) throw error;

    // Extract the tradesperson data
    return data.map(item => item.tradesperson) as Tradesperson[];
  },
  [], // Fallback value
  'Failed to load recent tradespeople'
);

/**
 * Gets tradespeople saved by a specific user
 * @param userId The user whose saved tradespeople to retrieve
 * @returns Promise containing the user's saved tradespeople
 */
export const getUserSharedTradespeople = withErrorHandling(
  async (userId: string) => {
    const { data, error } = await supabase
      .from("user_tradespeople")
      .select(`
        *,
        tradesperson:tradesperson_id(*)
      `)
      .eq("user_id", userId)
      .order('created_at', { ascending: false });

    if (error) throw error;

    // Extract the tradesperson data
    return data.map(item => item.tradesperson) as Tradesperson[];
  },
  [], // Fallback value
  'Failed to load user tradespeople'
);

/**
 * Gets tradespeople by username
 * PERFORMANCE OPTIMIZATION: We convert username to lowercase to leverage the idx_profiles_username_ci
 * index created in 20250515000000_case_insensitive_usernames.sql. This provides a significant
 * performance improvement over using ILIKE for case-insensitive matching.
 *
 * @param username The username whose tradespeople to retrieve
 * @returns Promise containing the user's tradespeople with their profile info
 */
export const getTradesPeopleByUsername = withErrorHandling(
  async (username: string) => {
    // First get the user id from the username using case-insensitive matching with the indexed LOWER function
    const { data: userData, error: userError } = await supabase
      .from("profiles")
      .select("id, postcode")
      .eq('username', username.toLowerCase())
      .single();

    if (userError) throw userError;
    if (!userData) return { tradespeople: [], userData: null };

    // Then get their tradespeople
    const { data: tradeData, error: tradeError } = await supabase
      .from("user_tradespeople")
      .select(`
        *,
        tradesperson:tradesperson_id(*)
      `)
      .eq("user_id", userData.id)
      .order('created_at', { ascending: false });

    if (tradeError) throw tradeError;

    // Extract the tradesperson data
    return {
      tradespeople: tradeData.map(item => item.tradesperson) as Tradesperson[],
      userData: {
        ...userData,
        location: userData.postcode // Map postcode to location for compatibility
      }
    };
  },
  { tradespeople: [], userData: null }, // Fallback value
  'Failed to load user tradespeople by username'
);

/**
 * Gets popular tradespeople based on how many users have saved them
 * @param limit Maximum number of results to return
 * @returns Promise containing popular tradespeople
 */
export const getPopularTradespeople = withErrorHandling(
  async (limit = 10) => {
    const { data, error } = await supabase
      .from("tradespeople")
      .select(`
        *,
        user_tradespeople(id)
      `)
      .eq("verified", true)
      .limit(limit);

    if (error) throw error;

    // Sort by number of recommendations (number of users who saved this tradesperson)
    return data
      .map(trade => ({
        ...trade,
        recommendation_count: trade.user_tradespeople ? trade.user_tradespeople.length : 0
      }))
      .sort((a, b) => (b.recommendation_count || 0) - (a.recommendation_count || 0)) as Tradesperson[];
  },
  [], // Fallback value
  'Failed to load popular tradespeople'
);

/**
 * Searches for a tradesperson by contact information (phone or email only)
 * @param phone - Optional phone number to search by
 * @param email - Optional email to search by
 * @param category - Optional category to filter by (secondary filter)
 * @returns Promise containing the matching tradesperson or null
 */
export const searchTradeByContact = withErrorHandling(
  async (phone?: string, email?: string, category?: string) => {
    // Only proceed if we have either phone or email - we don't match by name
    if (!phone && !email) return null;

    // Create a logger for better diagnostics
    const logPrefix = "TRADESPERSON_SEARCH";
    debug.log(`${logPrefix}: Starting search with phone=${phone}, email=${email}, category=${category}`);

    // Start with an 'or' condition to combine phone and email filters
    const conditions = [];

    if (phone) {
      // Normalize phone number by removing all non-digit characters
      const normalizedPhone = phone.replace(/\D/g, '');
      debug.log(`${logPrefix}: Normalized phone number: ${normalizedPhone}`);

      // Create all possible formats to search
      const phoneFormats = [];

      // Original format
      phoneFormats.push(phone);

      // Normalized format
      phoneFormats.push(normalizedPhone);

      // Handle international format conversion
      if (normalizedPhone.startsWith('44')) {
        // Add version with leading 0 instead of 44
        phoneFormats.push('0' + normalizedPhone.substring(2));
        phoneFormats.push('+44' + normalizedPhone.substring(2));
      } else if (normalizedPhone.startsWith('0')) {
        // Add version with 44 instead of leading 0
        phoneFormats.push('44' + normalizedPhone.substring(1));
        phoneFormats.push('+44' + normalizedPhone.substring(1));
      }

      debug.log(`${logPrefix}: Searching for phone formats:`, phoneFormats);

      // Phone conditions
      for (const format of phoneFormats) {
        conditions.push(`phone.eq.${format}`);
        conditions.push(`phone.ilike.%${format}%`);
      }
    }

    if (email && email.trim() !== '') {
      // For email, use case-insensitive exact matching
      // Make sure to use the proper ILIKE pattern with % for wildcard matching
      const trimmedEmail = email.trim();
      conditions.push(`email.ilike.${trimmedEmail}`);
      conditions.push(`email.ilike.%${trimmedEmail}%`); // Also try with wildcards
    }

    // Start the query with our combined phone/email conditions
    let query = supabase
      .from("tradespeople")
      .select("*")
      .or(conditions.join(','));

    // Apply category filter separately (as an AND condition)
    if (category) {
      query = query.eq("category", category);
    }

    const { data, error } = await query.limit(10);

    if (error) {
      debug.error(`${logPrefix}: Search error:`, error);
      throw error;
    }

    debug.log(`${logPrefix}: Search results:`, data?.length || 0);

    // If we have results, take the first one
    if (data && data.length > 0) {
      debug.log(`${logPrefix}: Match found! ID: ${data[0].id}`);
      return (data[0] as Tradesperson);
    }

    debug.log(`${logPrefix}: No matches found.`);
    return null;
  },
  null, // Fallback value
  'Failed to search for tradesperson'
);

/**
 * Checks if a tradesperson is already saved by a specific user
 * @param userId - The user to check for
 * @param tradespersonId - The tradesperson to check
 * @returns Promise containing whether the tradesperson is saved
 */
export const isTradesPersonSavedByUser = withErrorHandling(
  async (userId: string, tradespersonId: string) => {
    const { data, error } = await supabase
      .from("user_tradespeople")
      .select("id")
      .eq("user_id", userId)
      .eq("tradesperson_id", tradespersonId)
      .maybeSingle();

    if (error) throw error;
    return !!data; // Convert to boolean - true if found, false if not
  },
  false, // Fallback value
  'Failed to check if tradesperson is saved'
);
